import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    AppBar,
    Toolbar,
    Box,
    Button,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    useTheme,
    useMediaQuery,
    Menu,
    MenuItem,
    Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LanguageIcon from "@mui/icons-material/Language";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useTranslation } from "react-i18next";
import { shineSweep } from "../utils/motion";

const FLAGS = { en: "🇬🇧", bm: "🇲🇾", cn: "🇨🇳" };
const NAMES = { en: "English", bm: "Bahasa Malaysia", cn: "中文" };
const ROMAN_DRAWER = "I";

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState("");
    const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const languageOpen = Boolean(languageAnchorEl);
    // `i18n.language` may be a full BCP-47 locale (e.g. `en-US`). Strip the
    // region so it matches our `en`/`bm`/`cn` dictionary keys for the
    // selected state, and renders as a compact "EN" pill.
    const currentLanguage = (i18n.language || "en").split("-")[0].toLowerCase();

    const handleLanguageMenu = (e) => setLanguageAnchorEl(e.currentTarget);
    const handleLanguageClose = () => setLanguageAnchorEl(null);
    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        handleLanguageClose();
    };

    const navItems = [
        { label: t("header.navItems.about"), id: "about" },
        { label: t("header.navItems.ourStory"), id: "our-story" },
        { label: t("header.navItems.services"), to: "/services" },
        { label: t("header.navItems.products"), id: "products" },
        { label: t("header.navItems.contact"), id: "contact" },
    ];

    useEffect(() => {
        const SECTION_IDS = navItems.filter((n) => n.id).map((n) => n.id);
        // Cache the section nodes once instead of `getElementById` per tick.
        // Lazy-loaded sections may not exist on first render, so we refresh
        // the cache after a short beat and on resize.
        let nodes = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
        const refreshTimer = setTimeout(() => {
            nodes = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
        }, 1500);

        let ticking = false;
        let lastScrolled = false;
        let lastActive = "";
        const handleScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                const next = window.scrollY > 30;
                if (next !== lastScrolled) {
                    lastScrolled = next;
                    setIsScrolled(next);
                }
                if (location.pathname === "/") {
                    let current = "";
                    const half = window.innerHeight / 2;
                    for (const el of nodes) {
                        if (el.getBoundingClientRect().top <= half) current = el.id;
                    }
                    if (current !== lastActive) {
                        lastActive = current;
                        setActiveSection(current);
                    }
                }
                ticking = false;
            });
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        const onResize = () => {
            nodes = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
        };
        window.addEventListener("resize", onResize, { passive: true });
        handleScroll();
        return () => {
            clearTimeout(refreshTimer);
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", onResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const scrollToSection = (id) => {
        if (location.pathname !== "/") {
            window.location.href = `/#${id}`;
            return;
        }
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
            setIsMenuOpen(false);
        }
    };

    // Lock the page behind the open drawer. `body { overflow: hidden }`
    // alone is not enough on iOS Safari — the page still scrolls under the
    // drawer. Pinning the body with `position: fixed` and restoring the
    // scroll offset on close is the standard iOS-safe technique.
    useEffect(() => {
        if (!isMenuOpen) return;
        const scrollY = window.scrollY;
        const body = document.body;
        const prev = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            overflow: body.style.overflow,
        };
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.left = "0";
        body.style.right = "0";
        body.style.width = "100%";
        body.style.overflow = "hidden";
        return () => {
            Object.assign(body.style, prev);
            window.scrollTo(0, scrollY);
        };
    }, [isMenuOpen]);

    const isActive = (item) => {
        if (item.to) return location.pathname === item.to;
        // Section links only light up via scroll-spy, which runs on the home
        // page. On other routes `activeSection` is stale, so gate it to "/".
        return location.pathname === "/" && activeSection === item.id;
    };

    /* Adapt header colors to scroll state — over hero (transparent dark) vs. cream sections */
    const isOverHero = !isScrolled;
    const navColor = isOverHero ? "rgba(255, 255, 255, 0.85)" : "#000000";
    // Active/hover accent must follow the same light-on-dark vs. dark-on-light
    // logic as `navColor`, otherwise active items render black over the dark
    // hero band and become invisible.
    const navColorAccent = isOverHero ? "#ffffff" : "#000000";

    /* Floating header: a contained pill that sits below the viewport top with
       margin on all sides, always frosted but with a tint that shifts slightly
       when scrolled so the navigation stays legible over light backgrounds.   */
    const floatBg = isOverHero ? "rgba(20, 20, 20, 0.42)" : "rgba(255, 255, 255, 0.7)";
    const floatBorder = isOverHero ? "1px solid rgba(255, 255, 255, 0.18)" : "1px solid rgba(255, 255, 255, 0.55)";
    const floatShadow = isOverHero
        ? "inset 0 1px 0 rgba(255,255,255,0.18), 0 18px 50px rgba(0,0,0,0.35)"
        : "inset 0 1px 0 rgba(255,255,255,0.65), 0 18px 50px rgba(0, 0, 0, 0.10)";

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    /* On mobile the header floats at the bottom of the viewport so
                       the brand and the Reserve action stay within thumb reach.
                       Desktop keeps the original top-anchored pill. */
                    top: { xs: "auto", md: "18px" },
                    bottom: { xs: "12px", md: "auto" },
                    left: "50%",
                    right: "auto",
                    transform: "translateX(-50%)",
                    width: { xs: "calc(100% - 24px)", md: "calc(100% - 36px)" },
                    maxWidth: "1280px",
                    background: "transparent",
                    boxShadow: "none",
                    transition: "all 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
            >
                <Toolbar
                    disableGutters
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "stretch",
                        padding: { xs: "0.55rem 0.75rem 0.55rem 1.1rem", md: "0.7rem 0.9rem 0.7rem 1.6rem" },
                        minHeight: { xs: "60px", md: "76px" },
                        background: floatBg,
                        backdropFilter: "blur(22px) saturate(1.4)",
                        WebkitBackdropFilter: "blur(22px) saturate(1.4)",
                        border: floatBorder,
                        borderRadius: { xs: "14px", md: "999px" },
                        boxShadow: floatShadow,
                        transition: "all 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                >
                    {/* ── Brand block ── */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, md: 2 } }}>
                        <Box
                            component={Link}
                            to="/"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                textDecoration: "none",
                                transition: "opacity 0.3s ease",
                                "&:hover": { opacity: 0.85 },
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/logo.png"
                                alt="Two Sense Hair & Beauty"
                                sx={{
                                    height: { xs: "36px", md: "42px" },
                                    width: "auto",
                                    objectFit: "contain",
                                    filter: isOverHero ? "brightness(0) invert(1)" : "none",
                                    transition: "filter 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                            />
                        </Box>

                        {/* Atelier tagline (desktop only) */}
                        <Box
                            sx={{
                                display: { xs: "none", lg: "flex" },
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.25,
                                pl: 1.75,
                                ml: 0.5,
                                borderLeft: `1px solid ${
                                    isOverHero ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)"
                                }`,
                                transition: "border-color 0.45s ease",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: '"Cormorant Garamond", serif',
                                    fontStyle: "italic",
                                    fontSize: "0.78rem",
                                    fontWeight: 400,
                                    color: navColor,
                                    lineHeight: 1.1,
                                    letterSpacing: "0.3px",
                                    transition: "color 0.45s ease",
                                }}
                            >
                                Hair & Beauty Atelier
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.55rem",
                                    fontWeight: 500,
                                    color: navColor,
                                    letterSpacing: "2.5px",
                                    textTransform: "uppercase",
                                    opacity: 0.7,
                                    transition: "color 0.45s ease",
                                }}
                            >
                                By Appointment
                            </Typography>
                        </Box>
                    </Box>

                    {/* ── Nav ── */}
                    {!isMobile && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: { md: 0.25, lg: 0.5 },
                                position: "relative",
                            }}
                        >
                            {navItems.map((item) => {
                                const active = isActive(item);
                                const buttonProps = item.to
                                    ? {
                                          component: Link,
                                          to: item.to,
                                          /* If we're already on this route, the URL doesn't change
                                             and ScrollToTop's effect won't fire — scroll explicitly. */
                                          onClick: () => {
                                              if (location.pathname === item.to) {
                                                  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                                              }
                                          },
                                      }
                                    : { onClick: () => scrollToSection(item.id) };
                                return (
                                    <Button
                                        key={item.to || item.id}
                                        {...buttonProps}
                                        disableRipple
                                        sx={{
                                            color: active ? navColorAccent : navColor,
                                            fontFamily: '"Inter", sans-serif',
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                            letterSpacing: "2.5px",
                                            textTransform: "uppercase",
                                            position: "relative",
                                            padding: { md: "0.55rem 0.85rem", lg: "0.6rem 1rem" },
                                            borderRadius: 0,
                                            transition: "color 0.35s ease",
                                            "&::after": {
                                                content: '""',
                                                position: "absolute",
                                                bottom: "10px",
                                                left: "50%",
                                                width: active ? "18px" : "0px",
                                                height: "1px",
                                                background: navColorAccent,
                                                transition: "width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                                transform: "translateX(-50%)",
                                            },
                                            "&:hover": {
                                                color: navColorAccent,
                                                backgroundColor: "transparent",
                                            },
                                            "&:hover::after": { width: "18px" },
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                );
                            })}
                        </Box>
                    )}

                    {/* ── Right cluster ── */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 0.5, md: 1 },
                            pl: { md: 1.5 },
                            borderLeft: {
                                xs: "none",
                                md: `1px solid ${isOverHero ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.14)"}`,
                            },
                            transition: "border-color 0.45s ease",
                        }}
                    >
                        <Button
                            onClick={handleLanguageMenu}
                            disableRipple
                            aria-label="Change language"
                            sx={{
                                color: navColor,
                                minWidth: "auto",
                                px: { xs: 1, md: 1.25 },
                                py: 0.75,
                                borderRadius: "999px",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.6,
                                transition: "color 0.3s ease, background 0.3s ease",
                                "&:hover": {
                                    backgroundColor: isOverHero
                                        ? "rgba(255,255,255,0.08)"
                                        : "rgba(0,0,0,0.05)",
                                    color: navColorAccent,
                                },
                            }}
                        >
                            <LanguageIcon sx={{ fontSize: "0.95rem" }} />
                            <Typography
                                sx={{
                                    fontSize: "0.68rem",
                                    fontWeight: 500,
                                    letterSpacing: "2px",
                                    whiteSpace: "nowrap",
                                    display: { xs: "none", sm: "block" },
                                }}
                            >
                                {currentLanguage.toUpperCase()}
                            </Typography>
                            <KeyboardArrowDownIcon
                                sx={{
                                    fontSize: "0.95rem",
                                    display: { xs: "none", sm: "block" },
                                    opacity: 0.7,
                                    transform: languageOpen ? "rotate(180deg)" : "none",
                                    transition: "transform 0.3s ease",
                                }}
                            />
                        </Button>
                        <Menu
                            anchorEl={languageAnchorEl}
                            open={languageOpen}
                            onClose={handleLanguageClose}
                            PaperProps={{
                                sx: {
                                    mt: isMobile ? 0 : 1,
                                    mb: isMobile ? 1 : 0,
                                    minWidth: 168,
                                    borderRadius: 0,
                                    background: "rgba(255, 255, 255, 0.75)",
                                    backdropFilter: "blur(22px) saturate(1.4)",
                                    WebkitBackdropFilter: "blur(22px) saturate(1.4)",
                                    border: "1px solid rgba(255, 255, 255, 0.6)",
                                    boxShadow:
                                        "inset 0 1px 0 rgba(255,255,255,0.65), 0 18px 50px rgba(0, 0, 0, 0.12)",
                                },
                            }}
                            transformOrigin={{
                                horizontal: "right",
                                vertical: isMobile ? "bottom" : "top",
                            }}
                            anchorOrigin={{
                                horizontal: "right",
                                vertical: isMobile ? "top" : "bottom",
                            }}
                        >
                            {Object.entries(FLAGS).map(([code, flag]) => (
                                <MenuItem
                                    key={code}
                                    onClick={() => changeLanguage(code)}
                                    selected={currentLanguage === code}
                                    sx={{
                                        py: 1.25,
                                        px: 2,
                                        "&.Mui-selected": {
                                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                                        },
                                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.06)" },
                                    }}
                                >
                                    <Box sx={{ fontSize: "1.1rem", mr: 1.25 }}>{flag}</Box>
                                    <Typography
                                        sx={{
                                            fontSize: "0.8rem",
                                            fontWeight: currentLanguage === code ? 600 : 400,
                                            color: "#000000",
                                        }}
                                    >
                                        {NAMES[code]}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>

                        <Button
                            component={Link}
                            to="/appointment"
                            disableRipple
                            endIcon={
                                <ArrowOutwardIcon
                                    sx={{
                                        fontSize: "0.95rem !important",
                                        transition: "transform 0.4s cubic-bezier(0.23,1,0.32,1)",
                                    }}
                                />
                            }
                            sx={{
                                display: isMobile ? "none" : "inline-flex",
                                fontFamily: '"Inter", sans-serif',
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                letterSpacing: "2.5px",
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                py: "0.7rem",
                                pl: "1.4rem",
                                pr: "1.1rem",
                                borderRadius: "999px",
                                background: isOverHero ? "#ffffff" : "#000000",
                                color: isOverHero ? "#000000" : "#ffffff",
                                border: `1px solid ${isOverHero ? "#ffffff" : "#000000"}`,
                                boxShadow: isOverHero
                                    ? "0 6px 18px rgba(0,0,0,0.18)"
                                    : "0 6px 18px rgba(0,0,0,0.12)",
                                transition: "all 0.4s cubic-bezier(0.23,1,0.32,1)",
                                /* Light sweep, in labeau_landing's idiom. Only on the
                                   scrolled state: over the hero this pill is white, and a
                                   white-on-white sweep is invisible. */
                                ...(isOverHero ? {} : shineSweep()),
                                "& .MuiButton-endIcon": { ml: 1, transition: "transform 0.4s ease" },
                                "&:hover": {
                                    background: isOverHero ? "#000000" : "#1a1a1a",
                                    borderColor: isOverHero ? "#000000" : "#1a1a1a",
                                    color: "#ffffff",
                                    "& .MuiButton-endIcon": { transform: "translate(2px, -2px)" },
                                },
                            }}
                        >
                            {t("header.buttons.bookNow")}
                        </Button>

                        {isMobile && (
                            <IconButton
                                onClick={() => setIsMenuOpen(true)}
                                aria-label="Open navigation menu"
                                sx={{
                                    color: navColor,
                                    transition: "color 0.3s ease",
                                    "&:hover": { color: "#000000", backgroundColor: "transparent" },
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Mobile drawer — premium bottom-sheet, no scrim */}
            {isMobile && (
                <Drawer
                    anchor="bottom"
                    open={isMenuOpen}
                    onClose={() => setIsMenuOpen(false)}
                    ModalProps={{ keepMounted: false }}
                    slotProps={{
                        backdrop: {
                            sx: {
                                background: "rgba(0, 0, 0, 0.55)",
                                backdropFilter: "blur(6px)",
                                WebkitBackdropFilter: "blur(6px)",
                            },
                        },
                    }}
                    PaperProps={{
                        sx: {
                            background: "#0e0e0e",
                            backgroundImage:
                                "radial-gradient(ellipse at 25% 0%, rgba(255,255,255,0.07) 0%, transparent 55%), radial-gradient(ellipse at 80% 100%, rgba(255,255,255,0.04) 0%, transparent 55%)",
                            borderTop: "1px solid rgba(255, 255, 255, 0.14)",
                            borderTopLeftRadius: "22px",
                            borderTopRightRadius: "22px",
                            boxShadow: "0 -22px 60px rgba(0,0,0,0.55)",
                            color: "#ffffff",
                            width: "100%",
                            maxHeight: "88vh",
                            // Without this, tall content (nav items + CTA + footer)
                            // can run off the bottom on small phones with no way to
                            // reach the CTA. Native momentum scrolling on iOS via
                            // webkit prop.
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            overscrollBehavior: "contain",
                        },
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            p: "1.5rem 1.65rem 2rem",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {/* Drag indicator */}
                        <Box
                            sx={{
                                width: 40,
                                height: 3,
                                borderRadius: 999,
                                background: "rgba(255,255,255,0.22)",
                                mx: "auto",
                                mb: 2,
                            }}
                        />
                        {/* ── Top bar: logo + close ── */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1.5,
                                pb: 2.25,
                                borderBottom: "1px solid rgba(255, 255, 255, 0.16)",
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/logo.png"
                                alt="Two Sense"
                                sx={{
                                    height: 32,
                                    objectFit: "contain",
                                    filter: "brightness(0) invert(1)",
                                }}
                            />
                            <IconButton
                                onClick={() => setIsMenuOpen(false)}
                                aria-label="Close navigation menu"
                                sx={{
                                    color: "rgba(255,255,255,0.85)",
                                    border: "1px solid rgba(255,255,255,0.18)",
                                    borderRadius: "50%",
                                    width: 36,
                                    height: 36,
                                    "&:hover": {
                                        background: "rgba(255,255,255,0.08)",
                                        color: "#ffffff",
                                    },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: "1.05rem" }} />
                            </IconButton>
                        </Box>

                        {/* ── Atelier eyebrow + tagline ── */}
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                sx={{
                                    fontSize: "0.55rem",
                                    fontWeight: 500,
                                    letterSpacing: "3.5px",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.55)",
                                    mb: 1,
                                }}
                            >
                                Hair & Beauty Atelier
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: '"Cormorant Garamond", serif',
                                    fontStyle: "italic",
                                    fontSize: "1rem",
                                    color: "rgba(255,255,255,0.85)",
                                    lineHeight: 1.55,
                                }}
                            >
                                — {t("header.about.description")}
                            </Typography>
                        </Box>

                        {/* ── Navigation label ── */}
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, mb: 1.5 }}>
                            <Typography
                                sx={{
                                    fontFamily: '"Cormorant Garamond", serif',
                                    fontStyle: "italic",
                                    fontSize: "0.85rem",
                                    color: "rgba(255,255,255,0.45)",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                {ROMAN_DRAWER}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.55rem",
                                    fontWeight: 500,
                                    letterSpacing: "3px",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.6)",
                                }}
                            >
                                Navigation
                            </Typography>
                            <Box
                                sx={{
                                    flex: 1,
                                    height: "1px",
                                    background: "rgba(255,255,255,0.16)",
                                    ml: 1,
                                }}
                            />
                        </Box>

                        {/* ── Nav items ── */}
                        <List sx={{ p: 0, mb: 2.25 }}>
                            {navItems.map((item, idx) => {
                                const active = isActive(item);
                                const buttonProps = item.to
                                    ? {
                                          component: Link,
                                          to: item.to,
                                          onClick: () => {
                                              setIsMenuOpen(false);
                                              if (location.pathname === item.to) {
                                                  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                                              }
                                          },
                                      }
                                    : { onClick: () => scrollToSection(item.id) };
                                const num = String(idx + 1).padStart(2, "0");
                                return (
                                    <ListItem key={item.to || item.id} disablePadding>
                                        <ListItemButton
                                            {...buttonProps}
                                            disableRipple
                                            sx={{
                                                borderRadius: 0,
                                                py: 1.15,
                                                px: 0,
                                                borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                                                display: "flex",
                                                alignItems: "baseline",
                                                gap: 2,
                                                color: "#ffffff",
                                                transition: "padding-left 0.4s cubic-bezier(0.23,1,0.32,1)",
                                                "&:hover": {
                                                    background: "transparent",
                                                    pl: 1.25,
                                                },
                                                "&:hover .nav-num": {
                                                    color: "rgba(255,255,255,0.9)",
                                                },
                                                "&:hover .nav-arrow": {
                                                    transform: "translate(2px, -2px)",
                                                    opacity: 1,
                                                },
                                            }}
                                        >
                                            <Typography
                                                className="nav-num"
                                                sx={{
                                                    fontFamily: '"Cormorant Garamond", serif',
                                                    fontStyle: "italic",
                                                    fontSize: "0.8rem",
                                                    color: active
                                                        ? "rgba(255,255,255,0.9)"
                                                        : "rgba(255,255,255,0.4)",
                                                    width: 24,
                                                    transition: "color 0.4s ease",
                                                }}
                                            >
                                                {num}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    flex: 1,
                                                    fontFamily: '"Cormorant Garamond", serif',
                                                    fontSize: "1.3rem",
                                                    fontWeight: 400,
                                                    color: active ? "#ffffff" : "rgba(255,255,255,0.92)",
                                                    letterSpacing: "0.3px",
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                            <ArrowOutwardIcon
                                                className="nav-arrow"
                                                sx={{
                                                    fontSize: "0.95rem",
                                                    color: "rgba(255,255,255,0.55)",
                                                    opacity: active ? 1 : 0.55,
                                                    transition:
                                                        "transform 0.4s cubic-bezier(0.23,1,0.32,1), opacity 0.3s ease",
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>

                        {/* ── Reserve CTA ── */}
                        <Button
                            component={Link}
                            to="/appointment"
                            onClick={() => setIsMenuOpen(false)}
                            disableRipple
                            endIcon={<ArrowOutwardIcon sx={{ fontSize: "1rem !important" }} />}
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontWeight: 500,
                                fontSize: "0.72rem",
                                letterSpacing: "2.5px",
                                textTransform: "uppercase",
                                py: "1rem",
                                borderRadius: "999px",
                                background: "#ffffff",
                                color: "#000000",
                                border: "1px solid #ffffff",
                                boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
                                transition: "all 0.4s cubic-bezier(0.23,1,0.32,1)",
                                display: "inline-flex",
                                justifyContent: "center",
                                "& .MuiButton-endIcon": {
                                    ml: 1,
                                    transition: "transform 0.4s ease",
                                },
                                "&:hover": {
                                    background: "#000000",
                                    color: "#ffffff",
                                    borderColor: "#ffffff",
                                    "& .MuiButton-endIcon": { transform: "translate(2px, -2px)" },
                                },
                            }}
                        >
                            {t("header.buttons.bookNow")}
                        </Button>

                        {/* ── Footer mark ── */}
                        <Box
                            sx={{
                                mt: 2.25,
                                pt: 2.25,
                                borderTop: "1px solid rgba(255, 255, 255, 0.08)",
                                display: "flex",
                                alignItems: "baseline",
                                justifyContent: "space-between",
                                gap: 1.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.55rem",
                                    fontWeight: 500,
                                    letterSpacing: "2.5px",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.45)",
                                }}
                            >
                                Bandar Jaya Putra · Johor Bahru
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: '"Cormorant Garamond", serif',
                                    fontStyle: "italic",
                                    fontSize: "0.78rem",
                                    color: "rgba(255,255,255,0.4)",
                                }}
                            >
                                est. 2024
                            </Typography>
                        </Box>
                    </Box>
                </Drawer>
            )}
        </>
    );
};

export default Header;
